import Icon from '@components/Icon';
import { CloseIcon } from '@components/Icons/CloseIcon';
import { MenuIcon } from '@components/Icons/MenuIcon';
import { SearchIcon } from '@components/Icons/SearchIcon';
import LowBalance from '@components/LowBalance';
import NapsterSpinner from '@components/NapsterSpinner';
import UserAvatar from '@components/UserAvatar';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { useStationMode } from '@hooks/station';
import {
  getAlbumById,
  getTrackById,
  quickSearch,
  SearchResult,
} from '@services/api';
import { useTranslation } from 'react-i18next';

const UI_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

import { sendSelectCompanionMessage } from '@utils/station';
import { extraQueryParams } from '@utils/constants';
import { getTrackUrlForLocale } from '@utils/localeUtils';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { placeHolderData } from '../../config';


export const Header = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activePage = location.pathname.split('/')[1] || 'home';
  const isStationMode = useStationMode();

  const { setSidebarOpen, sidebarOpen, setActivePopup } = useCommonState();
  const { activePlaylist, setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { secondsBalance } = useSessionFeatureState();
  const { isAuthenticated, signinRedirect } = useAuth();
  const [active, setActive] = useState(true);
  const { t, i18n } = useTranslation();
  const [localeDropdownOpen, setLocaleDropdownOpen] = useState(false);
  const localeDropdownRef = useRef<HTMLDivElement>(null);
  const currentLang = UI_LANGUAGES.find((l) => l.code === i18n.language) ?? UI_LANGUAGES[0];

  // Search state
  const [searchQuery, setSearchQuery] = useState(() =>
    location.pathname.startsWith('/search') ? searchParams.get('q') || '' : '',
  );
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const mobileSearchContainerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);

  // Show / hide based on scroll
  useEffect(() => {
    const content = document.getElementById('content-scroll');
    const handleScroll = () => {
      if (!content) return;
      const scrollY = content.scrollTop;
      setActive(scrollY <= 100);
    };
    if (content) {
      content.addEventListener('scroll', handleScroll);
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const openSidebar = () => {
    setSidebarOpen(true);
  };
  const handleSignIn = () => {
    signinRedirect({
      extraQueryParams,
    }).catch((error) => {
      console.error('Sign-in error:', error);
    });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        localeDropdownRef.current &&
        !localeDropdownRef.current.contains(event.target as Node)
      ) {
        setLocaleDropdownOpen(false);
      }
      // Close search dropdown when clicking outside
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSearchDropdown(false);
        setSelectedSearchIndex(-1);
      }
      // Close mobile search when clicking outside
      if (
        mobileSearchContainerRef.current &&
        !mobileSearchContainerRef.current.contains(event.target as Node)
      ) {
        setIsMobileSearchOpen(false);
        setShowSearchDropdown(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search functionality
  const performSearch = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchDropdown(false);
        setIsSearchLoading(false);
        return;
      }

      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      searchAbortRef.current = new AbortController();

      setIsSearchLoading(true);
      try {
        const results = await quickSearch(query);
        if (!searchAbortRef.current.signal.aborted) {
          setSearchResults(results);
          setShowSearchDropdown(isSearchFocused && query.trim().length > 0);
          setSelectedSearchIndex(-1);
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Search error:', error);
          setSearchResults([]);
          setShowSearchDropdown(false);
        }
      } finally {
        setIsSearchLoading(false);
      }
    },
    [isSearchFocused],
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);

      // Close dropdown immediately if query is empty
      if (!value.trim()) {
        setShowSearchDropdown(false);
        setSearchResults([]);
        setIsSearchLoading(false);
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        performSearch(value);
      }, 600);
    },
    [performSearch],
  );

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!searchQuery.trim()) {
        if (location.pathname.startsWith('/search')) {
          navigate('/');
        }
        return;
      }

      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
      setShowSearchDropdown(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`, {
        state: {
          from: `${location.pathname}${location.search}#search-input-header`,
        },
      });
    },
    [searchQuery, navigate, location.pathname, location.search],
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchDropdown(false);
    setIsSearchLoading(false);
    setSelectedSearchIndex(-1);

    if (location.pathname.startsWith('/search')) {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  const handleCloseMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(false);
    setShowSearchDropdown(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSearchIndex(-1);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
    if (searchQuery.trim()) {
      setShowSearchDropdown(true);
    }
  }, [searchQuery]);

  const handleSearchBlur = useCallback(() => {
    setIsSearchFocused(false);
  }, []);

  const handleSearchResultClick = useCallback(
    async (result: SearchResult) => {
      setShowSearchDropdown(false);
      setSearchQuery(searchParams.get('q') || '');

      try {
        if (result.type === 'Album') {
          const fullAlbumData = await getAlbumById(result.id);
          if (fullAlbumData?.tracks?.length) {
            const contributor = fullAlbumData.contributors?.[0];
            const firstTrack = fullAlbumData.tracks[0];

            if (contributor) {
              addRecentTrack({
                track: {
                  name: firstTrack.name,
                  url: getTrackUrlForLocale(firstTrack, preferredLocale),
                  trackIndex: 1,
                },
                contributor: {
                  id: fullAlbumData.id,
                  givenName: contributor.givenName || t('common.unknownArtist'),
                  thumbnailUrl:
                    contributor.thumbnailUrl ||
                    placeHolderData.contributorThumbnail,
                },
                albumArtUrl:
                  fullAlbumData?.thumbnailUrl ||
                  fullAlbumData?.albumArtUrl ||
                  contributor?.thumbnailUrl ||
                  placeHolderData.albumCover,
                timestamp: Date.now(),
              });
            }

            setActivePlaylist({ currentIndex: 0, album: fullAlbumData });
            setActivePopup('musicplayer');
          }
        } else if (result.type === 'Track') {
          const trackInfo = await getTrackById(result.id);
          if (trackInfo) {
            const trackWithUrls = {
              id: trackInfo.id,
              name: trackInfo.trackName,
              duration: 0,
              isLiked: trackInfo.isLiked ?? false,
              urls: trackInfo.audioUrl?.urls || [],
              url:
                trackInfo.audioUrl?.urls?.length > 0
                  ? undefined
                  : `${trackInfo.audioUrl?.path}/${trackInfo.audioUrl?.fileName}.mp3`,
            };

            const singleTrackAlbum = {
              id: trackInfo?.album?.id,
              albumName: trackInfo.album.name,
              albumArtUrl:
                trackInfo?.album?.thumbnailUrl || trackInfo?.album?.albumArtUrl,
              contributors: trackInfo.contributors,
              tracks: [trackWithUrls],
              category: 'favorites',
              likes: 0,
              isLiked: trackInfo.isLiked ?? false,
              upc: '',
              pk: '',
              description: '',
              genre: [],
              type: 'search',
            };

            const contributor = trackInfo.contributors[0];
            addRecentTrack({
              track: {
                name: trackInfo.trackName,
                url: getTrackUrlForLocale(trackWithUrls, preferredLocale),
                trackIndex: 1,
              },
              contributor: {
                id: contributor?.id,
                givenName: contributor?.givenName || t('common.unknownArtist'),
                thumbnailUrl:
                  contributor?.thumbnailUrl ||
                  placeHolderData.contributorThumbnail,
              },
              albumArtUrl:
                trackInfo?.album?.thumbnailUrl ||
                trackInfo?.album?.albumArtUrl ||
                contributor?.thumbnailUrl ||
                placeHolderData.albumCover,
              timestamp: Date.now(),
            });

            setActivePlaylist({ currentIndex: 0, album: singleTrackAlbum });
            setActivePopup('musicplayer');
          }
        } else if (result.type === 'Artist') {
          navigate(`/contributor/${result.id}`);
        } else if (result.type === 'Mentor') {
          if (isStationMode) {
            console.log('🎵 Selecting companion:', result.id);
            sendSelectCompanionMessage(result.id);
            return;
          }
          navigate(`/${result.slug}?start=true`);
        }
      } catch (error) {
        console.error('Error handling search result click:', error);
      }
    },
    [
      setActivePlaylist,
      setActivePopup,
      addRecentTrack,
      preferredLocale,
      navigate,
      isStationMode,
      searchParams,
    ],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showSearchDropdown || searchResults.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSearchIndex((prev) =>
            prev < searchResults.length - 1 ? prev + 1 : prev,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSearchIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (
            selectedSearchIndex >= 0 &&
            selectedSearchIndex < searchResults.length
          ) {
            handleSearchResultClick(searchResults[selectedSearchIndex]);
          } else {
            handleSearchSubmit(e);
          }
          break;
        case 'Escape':
          setShowSearchDropdown(false);
          setSelectedSearchIndex(-1);
          setIsMobileSearchOpen(false);
          if (searchInputRef.current) {
            searchInputRef.current.blur();
          }
          if (mobileSearchInputRef.current) {
            mobileSearchInputRef.current.blur();
          }
          break;
      }
    },
    [
      showSearchDropdown,
      searchResults,
      selectedSearchIndex,
      handleSearchResultClick,
      handleSearchSubmit,
    ],
  );

  return (
    <div
      id="header"
      className={`absolute top-0 left-0 h-[65px] w-full flex items-end transition-opacity duration-150 ${
        sidebarOpen ? 'z-[9]' : 'z-[10000]'
      }
   ${active ? 'opacity-100' : 'opacity-0'}`}>
      <div
        className={`flex h-[64px] w-full px-4 py-0 gap-[12px] ${
          isStationMode ? '' : 'sm:pl-[177px]'
        } ${
          (activePage === 'musicforyou' ||
            activePage === 'serenity-wellness' ||
            activePage === 'audiobooks' ||
            activePage === 'favorites' ||
            activePage === 'my-creations') &&
          !activePlaylist
            ? 'z-[99999]'
            : 'z-[99]'
        } `}>
        <div
          className={`flex gap-1.5 items-center px-0 ${
            isStationMode ? '' : 'md:px-8'
          } ${location.pathname === '/access-denied' ? 'hidden' : ''}`}>
          <button
            className={`flex h-fit menu-icon ${isStationMode ? '' : 'sm:hidden'}`}
            onClick={openSidebar}>
            <MenuIcon />
          </button>
          {!isStationMode && (
            <Link to="/" className="flex items-center sm:hidden">
              <img
                src="/assets/images/logo_napster_white.svg"
                alt="Napster"
                className="h-8 w-auto z-[1001]"
              />
            </Link>
          )}
          {/* <Link
            to="/"
            className={`header-navigation flex items-center gap-[6px] px-3 py-0 border rounded-full h-[36px] -mt-[3px] hover:border-[#fcfcfc] transition-all duration-500 ease-in-out font-[NapsterSans] ${
              activePage === 'home' ? 'border-[#fcfcfc]' : 'border-[#fcfcfc]/10'
            }  `}>
            <Icon name={getIcon('home')} size={16} color="#FFFFFF" />
            <span className="flex text-xs normal  font-semibold leading-[15.6px] text-[#fcfcfc] font-[NapsterSans] leading-[normal]">
              Home
            </span>
          </Link> */}
        </div>
        <div className="absolute right-4 sm:right-6 bottom-4 flex items-center gap-3">
          {/* Mobile Search Icon Button - Only visible on mobile */}
          {!isStationMode && (
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="flex sm:hidden items-center justify-center w-[32px] h-[32px] border border-[#fcfcfc]/20 rounded-full hover:border-[#fcfcfc]/40 hover:bg-[#fcfcfc]/5 transition-all duration-300 ease-in-out">
              <div className="w-4 h-4 flex items-center justify-center [&_svg]:w-4 [&_svg]:h-4">
                <SearchIcon />
              </div>
            </button>
          )}

          {/* Desktop Search Box - Hidden on mobile */}
          {!isStationMode && (
            <div className="relative hidden sm:block">
              <form
                onSubmit={handleSearchSubmit}
                className="relative z-[10002]">
                <div
                  className={`flex items-center gap-1.5 px-2.5 h-[32px] border rounded-full hover:border-[#fcfcfc]/40 hover:bg-[#fcfcfc]/5 transition-[background-color,border-color] ease-in-out ${
                    showSearchDropdown && !isMobileSearchOpen
                      ? 'bg-[#fcfcfc]/10 border-[#fcfcfc]/40 duration-300'
                      : 'border-[#fcfcfc]/20 duration-0'
                  }`}>
                  <div className="w-4 h-4 flex items-center justify-center [&_svg]:w-4 [&_svg]:h-4">
                    <SearchIcon />
                  </div>
                  <input
                    id="search-input-header"
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    placeholder={t('header.askOrSearch')}
                    className="bg-transparent text-white text-xs font-[NapsterSans] placeholder:text-white/60 outline-none w-[300px]"
                    autoComplete="off"
                  />
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {isSearchLoading ? (
                      <div className="w-4 h-4 [&_div]:w-4 [&_div]:h-4">
                        <NapsterSpinner size="small" />
                      </div>
                    ) : (
                      searchQuery && (
                        <button
                          type="button"
                          onClick={handleClearSearch}
                          className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-white/20 transition-colors">
                          <CloseIcon
                            width={12}
                            height={12}
                            fillOpacity={1}
                            fillColor="white"
                          />
                        </button>
                      )
                    )}
                  </div>
                </div>
              </form>

              {/* Desktop Search Dropdown */}
              {showSearchDropdown && !isMobileSearchOpen && (
                <div
                  ref={searchDropdownRef}
                  className={`absolute -top-[1px] left-0 right-0 mt-0.5 bg-[#1a1a1a] border border-white/10 rounded-t-2xl shadow-lg z-[10001] w-full ${
                    isSearchLoading && searchResults.length === 0
                      ? 'h-[32px] rounded-b-2xl overflow-hidden pt-0'
                      : 'rounded-b-lg overflow-y-auto max-h-[400px] pt-8'
                  }`}>
                  {searchResults.length > 0 ? (
                    <>
                      {searchResults.map((result, index) => (
                        <div
                          key={`${result.type}-${result.id}`}
                          className={`flex gap-3 items-center p-3 cursor-pointer transition-colors hover:bg-white/10 ${
                            index === selectedSearchIndex ? 'bg-white/10' : ''
                          }`}
                          onClick={() => handleSearchResultClick(result)}>
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-white/10">
                            <img
                              src={
                                result.albumArtUrl ||
                                result.thumbnailUrl ||
                                placeHolderData.albumCover
                              }
                              alt={result.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  placeHolderData.albumCover;
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h3 className="text-white font-medium text-sm truncate">
                                {result.name}
                              </h3>
                              <span
                                className={`px-1.5 py-0.5 text-[10px] rounded-full flex-shrink-0 ${
                                  result.type === 'Album'
                                    ? 'bg-blue-500/20 text-blue-300'
                                    : result.type === 'Track'
                                    ? 'bg-green-500/20 text-green-300'
                                    : result.type === 'Mentor'
                                    ? 'bg-orange-500/20 text-orange-300'
                                    : 'bg-purple-500/20 text-purple-300'
                                }`}>
                                {result.type === 'Artist'
                                  ? t('header.contributor')
                                  : result.type === 'Mentor'
                                  ? t('header.companion')
                                  : result.type}
                              </span>
                            </div>
                            {result.contributors &&
                              result.contributors.length > 0 && (
                                <p className="text-white/60 text-xs truncate text-left">
                                  by {result.contributors[0].givenName}
                                </p>
                              )}
                            {result.type === 'Track' && result.albumName && (
                              <p className="text-white/40 text-xs truncate">
                                from {result.albumName}
                              </p>
                            )}
                            {result.type === 'Mentor' && result.jobTitle && (
                              <p className="text-white/60 text-xs truncate text-left">
                                {result.jobTitle}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                      <div
                        className="flex items-center justify-center p-3 cursor-pointer hover:bg-white/10 transition-colors border-t border-white/10"
                        onClick={handleSearchSubmit as any}>
                        <span className="text-white/80 text-sm">
                          {t('header.seeAllResults', { query: searchQuery })}
                        </span>
                      </div>
                    </>
                  ) : (
                    !isSearchLoading &&
                    searchQuery.trim() && (
                      <div className="p-6 text-center">
                        <div className="flex justify-center">
                          <SearchIcon />
                        </div>
                        <h3 className="text-white text-sm font-medium mt-3 mb-1">
                          {t('header.noContentFound', { query: searchQuery })}
                        </h3>
                        <p className="text-white/60 text-xs">
                          {t('header.checkSpelling')}
                        </p>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {/* Locale Dropdown */}
          <div className="relative" ref={localeDropdownRef}>
            <button
              onClick={() => setLocaleDropdownOpen(!localeDropdownOpen)}
              className="flex items-center gap-1.5 px-2.5 h-[32px] border border-[#fcfcfc]/20 rounded-full hover:border-[#fcfcfc]/40 hover:bg-[#fcfcfc]/5 transition-all duration-300 ease-in-out">
              <span className="text-base">{currentLang.flag}</span>
              <span className="text-xs font-semibold text-[#fcfcfc] font-[NapsterSans] hidden sm:inline">
                {currentLang.code.toUpperCase()}
              </span>
              <svg
                className={`w-3 h-3 text-white transition-transform ${
                  localeDropdownOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {localeDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-lg overflow-y-auto z-[10001] min-w-[180px] max-h-[300px]">
                {UI_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLocaleDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 transition-colors ${
                      currentLang.code === lang.code ? 'bg-white/5' : ''
                    }`}>
                    <span className="text-base">{lang.flag}</span>
                    <span className="text-sm text-white">{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {isAuthenticated ? (
            <div className="flex gap-[8px] items-center">
              {/* Low Balance Warning - only show when balance is low */}
              {secondsBalance !== null && secondsBalance < 300 && (
                <LowBalance balance={secondsBalance} />
              )}
              <UserAvatar size={36} />
            </div>
          ) : (
            <button
              onClick={handleSignIn}
              className="flex items-center gap-2 px-[9px] py-2 border border-[#fcfcfc]/20 rounded-full hover:border-[#fcfcfc]/40 hover:bg-[#fcfcfc]/5 transition-all duration-300 ease-in-out">
              <Icon name="people-1" size={16} color="#FFFFFF" />
              <span className="text-xs font-semibold text-[#fcfcfc] font-[NapsterSans]">
                {t('header.signIn')}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Search Lightbox Overlay */}
      {!isStationMode && isMobileSearchOpen && (
        <div
          ref={mobileSearchContainerRef}
          className="fixed top-[60px] left-0 right-0 z-[10003] sm:hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 -z-10"
            onClick={handleCloseMobileSearch}
          />

          {/* Search Container */}
          <div className="bg-[#1a1a1a] border-b border-white/10 shadow-lg">
            <form onSubmit={handleSearchSubmit} className="p-3">
              <div className="flex items-center gap-2 px-3 h-[40px] bg-white/5 border border-white/20 rounded-full">
                <div className="w-4 h-4 flex items-center justify-center [&_svg]:w-4 [&_svg]:h-4">
                  <SearchIcon />
                </div>
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  placeholder={t('header.askOrSearch')}
                  className="flex-1 bg-transparent text-white text-sm font-[NapsterSans] placeholder:text-white/60 outline-none"
                  autoComplete="off"
                  autoFocus
                />
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {isSearchLoading ? (
                    <div className="w-4 h-4 [&_div]:w-4 [&_div]:h-4">
                      <NapsterSpinner size="small" />
                    </div>
                  ) : searchQuery ? (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/20 transition-colors">
                      <CloseIcon
                        width={14}
                        height={14}
                        fillOpacity={1}
                        fillColor="white"
                      />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCloseMobileSearch}
                      className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/20 transition-colors">
                      <CloseIcon
                        width={14}
                        height={14}
                        fillOpacity={1}
                        fillColor="white"
                      />
                    </button>
                  )}
                </div>
              </div>
            </form>

            {/* Mobile Search Results */}
            {showSearchDropdown && (
              <div className="max-h-[60vh] overflow-y-auto">
                {searchResults.length > 0 ? (
                  <>
                    {searchResults.map((result, index) => (
                      <div
                        key={`mobile-${result.type}-${result.id}`}
                        className={`flex gap-3 items-center p-3 cursor-pointer transition-colors hover:bg-white/10 ${
                          index === selectedSearchIndex ? 'bg-white/10' : ''
                        }`}
                        onClick={() => {
                          handleSearchResultClick(result);
                          handleCloseMobileSearch();
                        }}>
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-white/10">
                          <img
                            src={
                              result.albumArtUrl ||
                              result.thumbnailUrl ||
                              placeHolderData.albumCover
                            }
                            alt={result.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                placeHolderData.albumCover;
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h3 className="text-white font-medium text-sm truncate">
                              {result.name}
                            </h3>
                            <span
                              className={`px-1.5 py-0.5 text-[10px] rounded-full flex-shrink-0 ${
                                result.type === 'Album'
                                  ? 'bg-blue-500/20 text-blue-300'
                                  : result.type === 'Track'
                                  ? 'bg-green-500/20 text-green-300'
                                  : result.type === 'Mentor'
                                  ? 'bg-orange-500/20 text-orange-300'
                                  : 'bg-purple-500/20 text-purple-300'
                              }`}>
                              {result.type === 'Artist'
                                ? t('header.contributor')
                                : result.type === 'Mentor'
                                ? t('header.companion')
                                : result.type}
                            </span>
                          </div>
                          {result.contributors &&
                            result.contributors.length > 0 && (
                              <p className="text-white/60 text-xs truncate text-left">
                                by {result.contributors[0].givenName}
                              </p>
                            )}
                          {result.type === 'Track' && result.albumName && (
                            <p className="text-white/40 text-xs truncate">
                              from {result.albumName}
                            </p>
                          )}
                          {result.type === 'Mentor' && result.jobTitle && (
                            <p className="text-white/60 text-xs truncate text-left">
                              {result.jobTitle}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    <div
                      className="flex items-center justify-center p-4 cursor-pointer hover:bg-white/10 transition-colors border-t border-white/10"
                      onClick={(e) => {
                        handleSearchSubmit(e);
                        handleCloseMobileSearch();
                      }}>
                      <span className="text-white/80 text-sm">
                        {t('header.seeAllResults', { query: searchQuery })}
                      </span>
                    </div>
                  </>
                ) : (
                  !isSearchLoading &&
                  searchQuery.trim() && (
                    <div className="p-6 text-center">
                      <div className="flex justify-center">
                        <SearchIcon />
                      </div>
                      <h3 className="text-white text-sm font-medium mt-3 mb-1">
                        {t('header.noContentFound', { query: searchQuery })}
                      </h3>
                      <p className="text-white/60 text-xs">
                        {t('header.checkSpelling')}
                      </p>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

Header.displayName = 'Header';
